import React, { useState, useRef, useMemo, useEffect } from "react";
import ReactDOM from "react-dom";
import { useParams } from "react-router-dom";
import {
  DownloadOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Mixpanel } from "src/services/mixpanel";
import { getDefaultFormattedDate, getFormattedSeekValue, getTraitsMap, roundOff } from "src/utils/helperFunctions";
import BucketDetails from "src/pages/OnePagerReport/V2/BucketDetails";
import useCreatedBucketsFromReport from "src/pages/OnePagerReport/V2/useCreatedBucketsFromReport";
import { ReportPdfContext } from "src/utils/contexts";
import { findChatSectionAndQuizSectionElements, findMainSectionElements } from "./helper";
import ReportHeader from "src/pages/OnePagerReport/Header";
import FitScore from "src/pages/OnePagerReport/FitScore";
import TraitsGrid from "src/pages/OnePagerReport/TraitsGrid";
import PersonalityComponent from "src/pages/OnePagerReport/PersonalityComponent/PersonalityComponent";
import { BUCKET_NAMES } from "src/constants/globalConstants";
import { TrustScore as TrustScoreIcon } from "src/assets/icons";
import { getTraitBand } from "src/pages/OnePagerReport/oprations";
import OverallSummary from "src/pages/OnePagerReport/V2/OverallSummary";
import CutoffTraitsTable from "src/pages/OnePagerReport/V2/CutoffTraitsTable";
import BehaviouralAnalysis from "src/pages/OnePagerReport/V2/BehaviouralAnalysis";
import CandidateApi from "src/services/apis/candidate";
import CandidateDetailsSection, { normalizeCandidateFormData } from "src/pages/Positions/CandidateFormData/CandidateDetailsSection";
import "src/pages/Positions/CandidateFormData/CandidateFormData.scss";

const A4_WIDTH_PX = 794; // 210mm in pixels (at ~3.8x scale)
const A4_HEIGHT_PX = 1123; // 297mm in pixels (at ~3.8x scale)
const CONTENT_WIDTH_PX = 1150; // New rendered content width
const CONTENT_HEIGHT_PX = 1626;
const PAGE_PADDING_PX = 92; // 20mm in pixels (left & right padding)

const DEVELOPMENT_ENV = process.env.REACT_APP_ENV === "development";
const IGNORED_PDF_TAGS = ["AUDIO", "VIDEO", "SOURCE"];

const getNumericPercentage = (value) => {
  const parsedValue = Number(value?.toString()?.split("%")?.[0])
  return Number.isFinite(parsedValue) ? parsedValue : null
}

/**
 * 
 * Component for generating and downloading a multi-page PDF report.
 * Extract relevant sections from the UI, organize them into A4 pages, and renders them
 * in an invisible container before converting them into a PDF using jsPDF and html2canvas.
 */

const ReportDownloadComponent = ({
  reportData,
  positionConfig,
  fetchSnapshots,
  referenceImageLink,
  hideFitScore,
  includeProctoring,
  onDownloadClick,
  isProctoringAvailable,
  onDownloadComplete,
  candidateFormData: candidateFormDataProp,
}) => {
  /***
   * hooks
   */
  const { userId, linkId } = useParams();

  /***
   * useStates
   */
  const [loading, setLoading] = useState(false); // Loading state

  const [showPortal, setShowPortal] = useState(false); // Show hidden components
  const [previewMode, setPreviewMode] = useState(false); // Toggle preview mode

  const [pages, setPages] = useState([]);
  const [candidateFormData, setCandidateFormData] = useState(null);
  const pageRefs = useRef([]); // References for generated PDF pages
  const hiddenContainerRef = useRef(null); // Reference for the hidden rendering container

  /**
   * custom hooks
   */
  const {
    functionalBuckets,
    communicationBuckets,
    competenciesBuckets,
    aptitudeBuckets,
    personalityBuckets,
    personalityBucketsIPIPLong,
    personalityBucketsIPIPShort,
    renderSpecialBuckets,
    otherBuckets,
  } = useCreatedBucketsFromReport({ reportData, reportPdfView: true })

  /**
   * useEffect
   */

  /*** Trigger PDF generation when pages to be downloaded are set ***/
  useEffect(() => {
    if (showPortal && reportData && pages.length) {
      // Forced timeout for save
      setTimeout(() => { savePdf() }, 1000)
    }
  }, [pages.length, reportData, showPortal])

  /*** Trigger PDF generation when user makes proctoring choice ***/
  useEffect(() => {
    if (includeProctoring !== null && includeProctoring !== undefined) {
      generatePDF();
    }
  }, [includeProctoring])

  useEffect(() => {
    if (candidateFormDataProp) {
      setCandidateFormData(candidateFormDataProp);
    }
  }, [candidateFormDataProp]);

  useEffect(() => {
    if (candidateFormDataProp === undefined) {
      fetchCandidateFormData();
    }
  }, [linkId, candidateFormDataProp]);

  /**
   * functions
   */

  const handleDownloadIconClick = () => {
    if (isProctoringAvailable && onDownloadClick) {
      onDownloadClick();
    } else {
      generatePDF();
    }
  };

  const fetchCandidateFormData = async () => {
    try {
      const response = await CandidateApi.getCandidateFormData(linkId)
      if (response.success) {
        setCandidateFormData(normalizeCandidateFormData(response.userData))
      }
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Generate and save the PDF document.
   * Use html2canvas to capture components and jsPDF to create the PDF.
   */
  const savePdf = async () => {
    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: [A4_WIDTH_PX, A4_HEIGHT_PX],
      });
      const start = performance.now()
      for (let i = 0; i < pageRefs.current.length; i++) {
        if (!pageRefs.current[i]) continue;


        const start1 = performance.now();
        const canvas = await html2canvas(pageRefs.current[i], {
          scale: 1.8,
          // Media elements can't be captured and <source> inside <audio>/<video>
          // has no computed style, which makes html2canvas throw "unexpected EOF".
          ignoreElements: (el) => IGNORED_PDF_TAGS.includes(el.tagName),
        });
        const imgData = canvas.toDataURL("image/jpeg", 0.6);
        const end1 = performance.now();
        if (DEVELOPMENT_ENV) console.log(
          "Time for canvas to Data : ",
          ((end1 - start1) / 1000).toFixed(2)
        );

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, 0, A4_WIDTH_PX, A4_HEIGHT_PX);
      }

      // Only add proctoring if user chose to include it
      if (includeProctoring) {
        const [nonCriticalSnapshots = [], criticalSnapshots = [], fetchedReferenceImageLink = referenceImageLink] = await (fetchSnapshots?.() ?? Promise.resolve([[], [], referenceImageLink]))
        const proctoringReferenceImageLink = fetchedReferenceImageLink || referenceImageLink
        if (nonCriticalSnapshots.length) {
          await addProctoring(pdf, nonCriticalSnapshots, "nonCritical", false, proctoringReferenceImageLink)
        }
        if (criticalSnapshots.length) {
          await addProctoring(pdf, criticalSnapshots, "critical", false, !nonCriticalSnapshots.length ? proctoringReferenceImageLink : null)
        }
        if (!nonCriticalSnapshots.length && !criticalSnapshots.length) {
          await addProctoring(pdf, [], "nonCritical", true, proctoringReferenceImageLink)
        }
      } else {
        await addProctoring(pdf, [], "nonCritical", true)
      }

      const end = performance.now()
      if (DEVELOPMENT_ENV) console.log(
        "Time FOR PDF GENERATION : ",
        ((end - start) / 1000).toFixed(2)
      );

      const fileNameParts = [reportData?.user?.name, reportData?.position?.name].filter(Boolean);
      pdf.save(`${fileNameParts.join(" - ") || "Report"} Report.pdf`);
    } catch (error) {
      console.error("Error while saving PDF:", error);
    } finally {
      setShowPortal(false);
      setLoading(false);
      onDownloadComplete?.();
    }
  };


  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 182, g: 182, b: 182 } // Default gray
  }

  const compressImage = async (imageUrl, maxWidth = 160, quality = 1) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate new dimensions maintaining aspect ratio
          const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
          canvas.width = img.width * ratio;
          canvas.height = img.height * ratio;
          
          // Draw and compress
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedDataUrl);
        } catch (error) {
          console.error('Image compression failed:', error);
          resolve(imageUrl); // Fallback to original URL
        }
      };

      setTimeout(() => {
        console.warn('Image compression timeout:', imageUrl);
        resolve(imageUrl); 
      }, 5000);
      
      img.src = imageUrl;
    });
  }

  const compressImagesParallel = async (snapshots) => {
    const promises = snapshots.map(snap => 
      compressImage(snap?.media?.link ?? snap?.snapshotLink)
    );
    return Promise.all(promises);
  }

  const addProctoring = async (doc, snapshots, type, showOnlyProctoringDetails = false, proctoringReferenceImageLink = null) => {

    const trustScore = reportData?.trustScore
    const tabSwitchedCount = reportData?.tabSwitched ?? 0
    const hideTrustScoreDescription = positionConfig?.skipAiProcOnCutoffFail && reportData?.cutOffCleared === false
    const trustScoreNum = getNumericPercentage(trustScore?.trustScore)
    const hasTrustScoreValue = trustScoreNum !== null
    const hasTrustScoreRemark = !!trustScore?.remark
    if (showOnlyProctoringDetails && !hasTrustScoreRemark && !isProctoringAvailable) return

    doc.addPage()

    const IMAGE_WIDTH = 150
    const IMAGE_HEIGHT = 150
    const PADDING_X = 50
    const PADDING_Y = 30
    const TITLE_HEIGHT = 50
    const FONT_SIZE = 12
    const LINE_HEIGHT = 12
    const SLOT_GAP = 20

    const DATE_TEXT_HEIGHT = LINE_HEIGHT + 4

    const DESC_MAX_LINES = 4
    const DESC_HEIGHT = DESC_MAX_LINES * LINE_HEIGHT

    let SLOT_HEIGHT = IMAGE_HEIGHT + DATE_TEXT_HEIGHT + SLOT_GAP
    let TITLE = "Snapshots"

    if (type === "critical") {
      SLOT_HEIGHT += DESC_HEIGHT
      TITLE = "Critical " + TITLE
    }

    let yOffset = PADDING_Y + 0

    doc.setTextColor(0, 0, 0)

    // Proctoring Details


    if (hasTrustScoreRemark || isProctoringAvailable) {
      doc.setFontSize(24)
      doc.setFont(undefined, "bold")
      doc.setTextColor(0, 0, 0)
      doc.text("Proctoring Details", PADDING_X, yOffset + 24)
      yOffset += 25 + 10

      if (hasTrustScoreValue || hasTrustScoreRemark) {
        doc.setFontSize(18)
        doc.setFont(undefined, "bold")
        doc.setTextColor(0, 0, 0)
        doc.text("Trust Score", PADDING_X, yOffset + 20)
        yOffset += 20 + 10
      }

      if (hasTrustScoreValue) {
        const bandStyle = getTraitBand({
          median: 50,
          lb: 25,
          ub: 75,
          score: roundOff(trustScoreNum),
        })
        const textColor = bandStyle.textColor
        const scoreText = `${roundOff(trustScoreNum)}%`
        const rgb = hexToRgb(textColor)
        doc.setTextColor(rgb.r, rgb.g, rgb.b)
        doc.setFontSize(14)
        doc.setFont(undefined, "normal")
        doc.text(scoreText, PADDING_X, yOffset + 18)
        yOffset += 18 + 15
      }

      if (!hideTrustScoreDescription && hasTrustScoreRemark) {
        doc.setTextColor(0, 0, 0)
        const remarkLines = doc.splitTextToSize(trustScore.remark, A4_WIDTH_PX - (2 * PADDING_X))
        remarkLines.forEach(line => {
          doc.text(line, PADDING_X, yOffset + 18)
          yOffset += 18
        })
        yOffset += 25
      }
    }

    if (isProctoringAvailable) {
      doc.setFontSize(14)
      doc.setFont(undefined, "bold")
      doc.setTextColor(0, 0, 0)
      doc.text(`Tabs Switched: ${tabSwitchedCount}`, PADDING_X, yOffset + 18)
      yOffset += 18 + 25
    }

    if (proctoringReferenceImageLink) {
      doc.setFontSize(18)
      doc.setFont(undefined, "bold")
      doc.setTextColor(0, 0, 0)
      doc.text("Reference Image", PADDING_X, yOffset + 20)
      yOffset += 30

      const compressedReferenceImage = await compressImage(proctoringReferenceImageLink, IMAGE_WIDTH)
      doc.addImage(compressedReferenceImage, "JPEG", PADDING_X, yOffset, IMAGE_WIDTH, IMAGE_HEIGHT)

      doc.setFontSize(FONT_SIZE)
      doc.setFont(undefined, "normal")
      doc.text("First verified photo", PADDING_X + 5, yOffset + IMAGE_HEIGHT + 14)
      yOffset += IMAGE_HEIGHT + DATE_TEXT_HEIGHT + 25
    }

    if (showOnlyProctoringDetails) return

    if (DEVELOPMENT_ENV) console.log(`Compressing ${snapshots.length} images...`);
    const compressionStart = performance.now();
    const compressedImages = await compressImagesParallel(snapshots);
    const compressionEnd = performance.now();
    if (DEVELOPMENT_ENV) console.log(
      `Image compression completed in ${((compressionEnd - compressionStart) / 1000).toFixed(2)}s`
    );

    doc.setFontSize(24)
    doc.setFont(undefined, "bold")

    doc.text(TITLE, PADDING_X, yOffset + 20)
    yOffset += TITLE_HEIGHT

    doc.setFontSize(FONT_SIZE)
    doc.setFont(undefined, "normal")

    const contentWidth = A4_WIDTH_PX - 2 * PADDING_X
    const contentHeightFirstPage = A4_HEIGHT_PX - yOffset - PADDING_Y //yOffset
    const contentHeightOtherPages = A4_HEIGHT_PX - 2 * PADDING_Y

    const SLOT_WIDTH = IMAGE_WIDTH + SLOT_GAP

    const imagesPerRow = Math.floor((contentWidth + SLOT_GAP) / SLOT_WIDTH)
    const imagesPerColumnFirstPage = Math.floor((contentHeightFirstPage + SLOT_GAP) / SLOT_HEIGHT)
    const imagesPerColumnOtherPages = Math.floor((contentHeightOtherPages + SLOT_GAP) / SLOT_HEIGHT)

    let pageIndex = 0
    let imagesPerColumn = imagesPerColumnFirstPage
    let imagesPerPage = imagesPerRow * imagesPerColumn
    let positionInPage = 0

    const addCriticalImgData = ({ imgObj, x, y }) => {
      const criticalText = imgObj?.aiProctoringData?.remark
      const desc = doc.splitTextToSize(criticalText ?? "", IMAGE_WIDTH - 10)
      const linesToShow = desc.slice(0, DESC_MAX_LINES)
      const descY = y + IMAGE_HEIGHT + DATE_TEXT_HEIGHT

      linesToShow.forEach((line, i) => {
        doc.text(line, x + 5, descY + (i + 1) * LINE_HEIGHT)
      })
    }

    const getRowAndCol = (position, rowLength) => ([Math.floor(position / rowLength), position % rowLength])
    const getXAndY = (col, row) => ([PADDING_X + col * SLOT_WIDTH, yOffset + row * SLOT_HEIGHT])

    let globalImageIndex = 0
    let currentPageImages = 0
    let maxImagesOnCurrentPage = imagesPerColumnFirstPage * imagesPerRow

    snapshots.forEach((imgObj, index) => {
      if (currentPageImages >= maxImagesOnCurrentPage) {
        doc.addPage()
        pageIndex += 1
        yOffset = PADDING_Y + TITLE_HEIGHT
        currentPageImages = 0
        maxImagesOnCurrentPage = imagesPerColumnOtherPages * imagesPerRow
      }

      // Calculate position on current page
      const [row, col] = getRowAndCol(currentPageImages, imagesPerRow)
      const [x, y] = getXAndY(col, row)

      const compressedImageUrl = compressedImages[globalImageIndex] || (imgObj?.media?.link ?? imgObj?.snapshotLink)
      doc.addImage(compressedImageUrl, "JPEG", x, y, IMAGE_WIDTH, IMAGE_HEIGHT)
      doc.text(imgObj?.createdAt ? `${getDefaultFormattedDate(imgObj?.createdAt, "lll")} IST` : getFormattedSeekValue(imgObj?.timestamp), x + 5, y + IMAGE_HEIGHT + 14)

      if (type === "critical") {
        addCriticalImgData({ imgObj, x, y })
      }

      globalImageIndex++
      currentPageImages++
    })
  }

  /**
   * 
   * List of extracted elements to be added to pages for pdf generation
   * on basis of their heights
   */
  const calculatePagesForPdf = (elementsForHeightCalculation) => {
    let currentPage = [];
    let remainingHeight = CONTENT_HEIGHT_PX - PAGE_PADDING_PX * 3;
    const newPages = [];

    elementsForHeightCalculation.forEach(({
      element,
      height,
      startWithNewPage = false,
      isChatBucketContainer = false,
      isQuestionsBucketContainer = false,
      useOuterHTML = false
    }) => {

      if (isChatBucketContainer) {
        const chatDetailElements = Array.from(
          element.querySelectorAll(".chat-detail-modal.report-pdf")
        );
        const multiChatDetailElements = Array.from(
          element.querySelectorAll(".multichat-detail-modal.report-pdf")
        );
        // Temporarily hide chat detail elements instead of removing them
        chatDetailElements?.forEach((chatElement) => {
          chatElement.style.visibility = "hidden";
        });

        multiChatDetailElements?.forEach((chatElement) => {
          chatElement.style.visibility = "hidden";
        });
      }
      if (isQuestionsBucketContainer) {
        // pass
      }

      if (startWithNewPage) {
        if (currentPage.length) newPages.push(currentPage);
        currentPage = [];
        remainingHeight = CONTENT_HEIGHT_PX - PAGE_PADDING_PX * 3;
      }

      // If element is larger than a page, place it alone on a new page
      if (height > CONTENT_HEIGHT_PX - PAGE_PADDING_PX * 3) {
        if (currentPage.length) {
          newPages.push(currentPage);
          currentPage = [];
        }
        newPages.push([{ element, height: CONTENT_HEIGHT_PX - PAGE_PADDING_PX * 3, useOuterHTML }]);
        remainingHeight = CONTENT_HEIGHT_PX - PAGE_PADDING_PX * 3;
        return;
      }

      // If not enough space, start a new page
      if (currentPage.length && remainingHeight < height) {
        newPages.push(currentPage);
        currentPage = [];
        remainingHeight = CONTENT_HEIGHT_PX - PAGE_PADDING_PX * 3;
      }

      // Add element to current page
      currentPage.push({ element, height, useOuterHTML });
      remainingHeight -= height;
    });

    if (currentPage.length) newPages.push(currentPage);
    setPages(newPages);
  }

  const generatePDF = async () => {
    setLoading(true);
    setPages([]);
    pageRefs.current = [];

    setShowPortal(true);
    // Wait for React to render hidden content
    await new Promise((resolve) => setTimeout(resolve, 3000));

    let elements = [];

    try {
      const mainSection =
        hiddenContainerRef.current.querySelector(".main-section");
      elements = [...elements, ...findMainSectionElements(mainSection)];

      const bucketContainers =
        hiddenContainerRef.current.querySelectorAll(".bucket-container");

      for (let i = 0; i < bucketContainers.length; i++) {
        const bucketContainer = bucketContainers[i];
        elements = [...elements, ...findChatSectionAndQuizSectionElements(bucketContainer)]
      }



      calculatePagesForPdf(elements);
      if (!elements.length) {
        setLoading(false);
        setShowPortal(false);
        onDownloadComplete?.();
      }
    } catch (error) {
      setLoading(false);
      console.error("Error during PDF generation:", error);
      setShowPortal(false);
    }
  };
  const logMixpanelEvent = (event) => {
    // Mixpanel Event
    Mixpanel.track(event, {
      "Candidate Name": reportData?.user?.name || reportData?.user?.email,
      "Position Title": reportData?.position?.name,
      Company: reportData?.position?.organizationName,
      Type: reportData?.position?.type,
      "Candidate Fit Score": roundOff(reportData.fitScore.score),
    });
  };

  const getBucketData = (bucketId) => {
    return bucketId
      ? [
        ...functionalBuckets?.buckets,
        ...communicationBuckets?.buckets,
        ...competenciesBuckets?.buckets,
        ...aptitudeBuckets?.buckets,
        ...otherBuckets,
      ].find(({ id }) => id === bucketId)
      : null;
  };

  /**
   * useMemo
   */
  const isNonDnaReport = useMemo(() => {
    return !!reportData?.position?.type && reportData?.position?.type !== "dna";
  }, [reportData]);

  const TrustScore = ({ trustScore }) => {
    const trustScoreNum = getNumericPercentage(trustScore?.trustScore)
    if (!trustScore?.remark || trustScoreNum === null) return <></>

    const bandStyle = getTraitBand({
      median: 50,
      lb: 25,
      ub: 75,
      score: roundOff(trustScoreNum),
    })
    const textColor = bandStyle.textColor
    const bgColor = bandStyle.color

    return (
      <div className="trustscore-section flex flex-col items-start">
        <div className="flex items-center mb-10">
          <div
            className="score-pill width-68 pl-2 pr-4 py-2 b-20 flex items-center text-12 select-none"
            style={{
              background: "#F3F3F3",
            }}
          >
            <span style={{ background: bgColor }} className="px-8 py-4 mr-4 b-40">
              <TrustScoreIcon color={textColor} />
            </span>
            <p style={{ color: textColor }}>{`${roundOff(trustScoreNum)}%`}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <ReportPdfContext.Provider value={{ reportPdfView: true }}>
      <div>
        {!loading ? (
          <DownloadOutlined
            onClick={handleDownloadIconClick}
            className="copy-url"
            title="Download Report"
          />
        ) : (
          <LoadingOutlined
            className="copy-url"
            title="Generating Report"
            disabled={true}
          />
        )}

        {/* Hidden Container */}
        {(showPortal || previewMode) &&
          ReactDOM.createPortal(
            <div
              ref={hiddenContainerRef}
              style={{
                display: "block",
                position: previewMode ? "relative" : "absolute",
                top: previewMode ? "auto" : "-9999px",
                left: previewMode ? "auto" : "-9999px",
                zIndex: previewMode ? 1000 : "auto",
                backgroundColor: previewMode ? "white" : "transparent",
              }}
            >
              <section className="main-section">
                <div id="one-page-report-component" className="v2">
                  <div className="info v2">
                    <ReportHeader
                      hideCta
                      reportData={reportData}
                      positionConfig={positionConfig}
                      userVideoSessionsList={[]}
                      isNonDnaReport={isNonDnaReport}
                      logMixpanelEvent={logMixpanelEvent}
                      hideVerdictPills={reportData?.cutOffCleared != null}
                    />
                    <div className="pdf-header-separator" />
                    <div className="content-wrapper">
                      <div className="max-width-left">
                        <div>
                          <CandidateDetailsSection
                            formData={candidateFormData}
                            hideWhenEmpty
                            reportView
                          />
                          {
                            reportData?.cutOffCleared != null ? (
                              <>
                                <OverallSummary reportData={reportData} positionConfig={positionConfig} />
                                <CutoffTraitsTable reportData={reportData} />
                                {reportData?.fitScore && reportData?.fitScore?.cutOff == null && (
                                  <div className="fit-score-grid">
                                    <TraitsGrid
                                      showBucketWise
                                      hideLegends
                                      title="Overall Score"
                                      description="Overall score based on performance across all assessed skills."
                                      traitsMap={getTraitsMap([{
                                        traitsMeta: {
                                          fitScore: {
                                            id: "overallScore",
                                            name: "Overall Score",
                                            displayName: "Overall Score",
                                            description: "Overall score",
                                            upperBound: { score: reportData.fitScore.data?.upperBound },
                                            lowerBound: { score: reportData.fitScore.data?.lowerBound },
                                            score: reportData.fitScore.score,
                                            median: reportData.fitScore.data?.median,
                                          }
                                        }
                                      }])}
                                    />
                                  </div>
                                )}
                              </>
                            ) : !hideFitScore ? (
                              <FitScore
                                {...reportData?.fitScore}
                                dnaScore={null}
                                showLegends={isNonDnaReport}
                                fitScoreMultiplier={positionConfig?.fitScoreMultiplier ?? 1}
                              />
                            ) : null
                          }
                          <BehaviouralAnalysis behaviouralAnalysis={reportData?.behaviouralAnalysis} />
                          {!!functionalBuckets.buckets?.length > 0 && (
                            <div className="single-bucket">
                              <div>
                                <h4 className="single-bucket--name">
                                  {functionalBuckets.name}
                                </h4>
                                <p className="single-bucket--description">
                                  {functionalBuckets.description}
                                </p>
                              </div>
                              <div>
                                {functionalBuckets.buckets.map((bucket) =>
                                  renderSpecialBuckets(bucket)
                                )}
                              </div>
                            </div>
                          )}
                          {!!communicationBuckets.buckets?.length > 0 && (
                            <div className="single-bucket">
                              <div>
                                <h4 className="single-bucket--name">
                                  {communicationBuckets.name}
                                </h4>
                                <p className="single-bucket--description">
                                  {communicationBuckets.description}
                                </p>
                              </div>
                              <div>
                                {communicationBuckets.buckets.map((bucket) =>
                                  renderSpecialBuckets(bucket)
                                )}
                              </div>
                            </div>
                          )}
                          {!!competenciesBuckets.buckets?.length > 0 && (
                            <div className="single-bucket">
                              <div>
                                <h4 className="single-bucket--name">
                                  {competenciesBuckets.name}
                                </h4>
                                <p className="single-bucket--description">
                                  {competenciesBuckets.description}
                                </p>
                              </div>
                              <div>
                                {competenciesBuckets.buckets.map((bucket) =>
                                  renderSpecialBuckets(bucket)
                                )}
                              </div>
                            </div>
                          )}
                          {!!aptitudeBuckets.buckets?.length > 0 && (
                            <div className="single-bucket">
                              <div>
                                <h4 className="single-bucket--name">
                                  {aptitudeBuckets.name}
                                </h4>
                                <p className="single-bucket--description">
                                  {aptitudeBuckets.description}
                                </p>
                              </div>
                              <div>
                                {aptitudeBuckets.buckets.map((bucket) =>
                                  renderSpecialBuckets(bucket)
                                )}
                              </div>
                            </div>
                          )}
                          {!!reportData &&
                            reportData.buckets?.map(
                              ({ id, name, description, traitsMeta }) =>
                                !Object.values(BUCKET_NAMES).includes(name) ? (
                                  <div key={id} className="group-container">
                                    <TraitsGrid
                                      showBucketWise
                                      hideLegends
                                      title={name}
                                      description={description}
                                      traitsMap={getTraitsMap([{ traitsMeta }])}
                                    />
                                  </div>
                                ) : null
                            )}
                          {!!personalityBuckets.traits?.length > 0 &&
                            <PersonalityComponent
                              bucket={personalityBuckets}
                            />
                          }
                          {!!personalityBucketsIPIPLong.traits?.length > 0 &&
                            <PersonalityComponent
                              bucket={personalityBucketsIPIPLong}
                            />
                          }
                          {!!personalityBucketsIPIPShort.traits?.length > 0 &&
                            <PersonalityComponent
                              bucket={personalityBucketsIPIPShort}
                            />
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section
                className="buckets-section v2"
                id="one-page-report-component"
              >
                <div className="info v2">
                  <div className="content-wrapper">
                    <div className="max-width-left">
                      {!!reportData &&
                        reportData.buckets?.map(({ id, name }) => {
                          const bucketData = getBucketData(id);
                          return bucketData ? (
                            <div key={id} className="bucket-container">
                              <BucketDetails
                                {...bucketData}
                                displayName={name}
                                reportData={reportData}
                                headerTraitColor={"grey"}
                                onBack={() => { }}
                                reportPdfView={true}
                                audioProctoring={positionConfig?.audioProctoring}
                              />
                            </div>
                          ) : null;
                        })}
                    </div>
                  </div>
                </div>
              </section>
              <TrustScore trustScore={reportData?.trustScore} />
            </div>,
            document.body
          )}

        {/* Render A4 pages with extracted elements */}
        <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
          {pages.map((pageElements, index) => (
            <A4PageWrapper
              key={index}
              ref={(el) => (pageRefs.current[index] = el)}
            >
              {pageElements.map(({ element, useOuterHTML }, idx) => {
                if (element.tagName?.toLowerCase() === "pre") {
                  return (
                    <pre key={idx} className={element.className}>
                      {element.textContent}
                    </pre>
                  );
                }

                return (
                  <div
                    key={idx}
                    className={element.className}
                    dangerouslySetInnerHTML={{
                      __html: useOuterHTML ? element.outerHTML : element.innerHTML,
                    }}
                  />
                );
              })}
            </A4PageWrapper>
          ))}
        </div>
      </div>
    </ReportPdfContext.Provider>
  );
};

export default ReportDownloadComponent;

const A4PageWrapper = React.forwardRef(({ children }, ref) => (
  <div
    ref={ref}
    className="report-download"
    style={{
      width: `${CONTENT_WIDTH_PX}px`,
      height: `${CONTENT_HEIGHT_PX}px`,
      padding: `${PAGE_PADDING_PX}px`,
      background: "white",
      overflow: "hidden",
      marginBottom: "20px",
      boxShadow: "0 0 5px rgba(0,0,0,0.1)",
      boxSizing: "border-box",
    }}
  >
    <style>
      {`
        .report-download .question-content h1, .report-download .question-content h2, .report-download .question-content h3,
        .report-download .question-content h4, .report-download .question-content h5, .report-download .question-content h6 {
          font-size: 16px !important;
        }
        .report-download .header {
          border-bottom: none !important;
          padding: 0 !important;
        }
        .report-download .pdf-header-separator {
          border-bottom: 1px solid #babacd;
          width: 100%;
          margin-bottom: 28px;
        }
        .report-download .behavioural-analysis {
          border-color: #e4e4e4;
        }
      `}
    </style>
    {children}
  </div>
));
